import { FlattenedNode, Node } from 'react-virtualized-tree';

import { SelectionWizardListName } from '../actions/phrasesSelectorWizard';

import { CampaignType, GroupType } from './domain';
import { GroupId } from './groups';

export interface PhrasesSelectorWizardGroupInfo {
    id: string;
    name: string;
    groupType: GroupType;
    campaign: {
        type?: CampaignType;
        name: string;
        id: string;
        strategy: {
            isAutoBudget: boolean;
        };
    };
    minusKeywords: string[];
    minusKeywordsLibraryList?: string[];
    regionIds: number[];
    startPhrases?: PhrasesSelectorListItem[];
}

export type PhrasesSelectorGroupKeywords = Record<string, boolean>;

export interface PhrasesSelectorGroupInfo extends PhrasesSelectorWizardGroupInfo {
    keywords: PhrasesSelectorGroupKeywords;
}

export type PhrasesSelectorGeneratorGroupPhrases = string;

export interface PhrasesSelectorPhraseInfo {
    shows: number;
}

export type PhrasesSelectorPhraseNodeStateMap = Record<
    keyof PhrasesSelectorStartPhraseNodeState,
    Record<string, boolean>
>;

export interface PhrasesSelectorStartPhraseNodeState {
    expanded: boolean;
    loading: boolean;
    selected: boolean;
    expandable: boolean;
}

export interface PhrasesSelectorStartPhraseNode extends Node {
    id: string;
    name: string;
    info: PhrasesSelectorPhraseInfo;
    state: PhrasesSelectorStartPhraseNodeState;
    children?: PhrasesSelectorStartPhraseNode[];
}

export interface PhrasesSelectorStartPhraseFlattenedNode extends FlattenedNode {
    info: PhrasesSelectorPhraseInfo;
    state: PhrasesSelectorStartPhraseNodeState;
}

export interface PhrasesSelectorStartPhrases {
    similar: Array<PhrasesSelectorStartPhraseNode>;
    nested: Array<PhrasesSelectorStartPhraseNode>;
}

export interface PhrasesSelectorGenerator {
    groups: Array<PhrasesSelectorGeneratorGroupPhrases>;
}

export interface PhrasesSelectorPhrase {
    name: string;
    shows: number;
}

export interface PhrasesSelectorRefinements {
    isPhrasesLoading: boolean;
    isPhraseShowsLoading: boolean;
    hasMoreRefineWords: boolean;
    phrasesError: string;
    phraseShowsError: string;
    refineWords: Array<PhrasesSelectorPhrase>;
    phrase: string;
    phraseShowsPrediction: number;
}

export enum KeywordsStatDictionaryKeys {
    searchedAlso = 'searchedAlso',
    searchedWith = 'searchedWith'
}

export interface KeywordsStatDictionary {
    [key: string]: {
        [KeywordsStatDictionaryKeys.searchedAlso]: PhrasesSelectorListItem[];
        [KeywordsStatDictionaryKeys.searchedWith]: PhrasesSelectorListItem[];
    };
}

export enum PhrasesSelectorWizardId {
    editGroupsPage = 'editGroupsPage',
    sideBar = 'sideBar',
    groupsGrid = 'groupsGrid'
}

export interface PhrasesSelectorState {
    groupInfo: PhrasesSelectorGroupInfo;
    isOpen: boolean;
    isLoading: boolean;
    startPhrases: PhrasesSelectorStartPhrases;
    startPhrasesError: string;
    refinements: PhrasesSelectorRefinements;
}

export interface PhrasesSelectorPersistentData {
    isExcludePhrases: boolean;
    manualValue: string;
    generator: PhrasesSelectorGenerator;
    keywords: Array<PhrasesSelectorPhrase>;
    minusPhrases: Array<string>;
    priceRate?: number;
    lastChange: string;
}

export type PhrasesSelectorsPersistentState = Record<GroupId, PhrasesSelectorPersistentData>;

export enum PhrasesSelectorInputColumns {
    MINUS_WORDS = 'minusWords',
    SEARCH_VALUE = 'searchValue',
    CHOSEN_WORDS = 'chosenWords'
}

export interface PhrasesSelectorWizardState {
    groupInfo: PhrasesSelectorWizardGroupInfo;
    isVisible: boolean;
    isInProgress: boolean;
    startPhrases: PhrasesSelectorListItem[];
    chosenPhrases: PhrasesSelectorListItem[];

    minusWordsList: PhrasesSelectorListItem[];
    columnsVisibility: {
        [SelectionWizardListName.similarPhrases]: boolean;
        [SelectionWizardListName.chosenPhrases]: boolean;
    };
    keywordsStat: KeywordsStatDictionary;
    phrasesSelection: {
        [key: string]: boolean;
    };

    inputValue: { [Key in PhrasesSelectorInputColumns]: string };
    errors: { [Key in PhrasesSelectorInputColumns]?: string };

    priceContext?: number;
}

export interface PhrasesSelectorColumnsVisibilityData {
    [SelectionWizardListName.similarPhrases]?: boolean;
    [SelectionWizardListName.chosenPhrases]?: boolean;
}

export interface ChangePhrasesSelectorWizardState {
    startPhrases?: PhrasesSelectorListItem[];
    keywordsStat?: {
        [key: string]: {
            searchedAlso: PhrasesSelectorListItem[];
            searchedWith: PhrasesSelectorListItem[];
        };
    };
    isInProgress: boolean;
    isVisible?: boolean;
}

export interface PhrasesSelectorListItem {
    text: string;
    selected: boolean;
    shows: number;
}

export enum PhrasesSelectorTreeUpdateType {
    add = 1,
    remove = 2,
    select = 3,
    expand = 4,
    fetchChildren = 5
}

export enum PhrasesSelectorStartPhrasesActions {
    addKeywords = 'addKeywords',
    remove = 'remove'
}

export enum PhrasesSelectorKeywordsActions {
    remove = 'remove'
}

export enum PhrasesSelectorMinusPhrasesActions {
    remove = 'remove'
}

export interface PhrasesUpdateResult {
    addItems: Array<string>;
}
